import * as fabric from 'fabric'
import store from '~/store/index'
import i18n from '~/helpers/i18n'

const HANDLE_RADIUS = 6
const HANDLE_COLOR = '#dc7633'

function cosLine (point1, point2) {
  return (point2.x - point1.x) / Math.sqrt((point2.x - point1.x) ** 2 + (point2.y - point1.y) ** 2)
}

function sinLine (point1, point2) {
  return (point2.y - point1.y) / Math.sqrt((point2.x - point1.x) ** 2 + (point2.y - point1.y) ** 2)
}

export default class Tape {
  constructor (objData = {}) {
    this.canvasObjects = [] // for canvas drawing
    this.requireNextClick = true // true - if necessary determinate more one vertex for object
    this.zOrder = objData.zOrder
    this.site = objData.site
    this.typeOf = 'tape'
    this.id = objData.id
    const handleParams = {
      radius: HANDLE_RADIUS,
      stroke: HANDLE_COLOR,
      fill: 'white',
      originX: 'center',
      originY: 'center',
      hoverCursor: 'nesw-resize',
      selectable: true,
      evented: true,
      hasBorders: false,
      hasControls: false,
      subTypeOf: 'handle',
      typeOf: this.typeOf,
      id: this.id
    }

    this.line = new fabric.Line([], {
      stroke: HANDLE_COLOR,
      strokeWidth: 0.5,
      opacity: 1,
      originX: 'center',
      originY: 'center',
      selectable: false,
      evented: false,
      hasBorders: false,
      hasControls: false,
      objectCaching: false,
      typeOf: this.typeOf,
      id: this.id
    })

    this.dist = new fabric.Textbox('', {
      fontFamily: 'Roboto',
      fontSize: 12,
      width: 100,
      textAlign: 'center',
      editable: false,
      cursorWidth: 0,
      originX: 'center',
      originY: 'bottom',
      selectable: false,
      evented: false,
      hasBorders: false,
      hasControls: false,
      typeOf: this.typeOf,
      id: this.id
    })

    this.handle1 = new fabric.Circle({ left: objData.x1 || Infinity, top: objData.y1 || Infinity, ...handleParams })
    this.handle2 = new fabric.Circle({ left: objData.x2 || Infinity, top: objData.y2 || Infinity, ...handleParams })

    this.handleLine = new fabric.Line([], {
      stroke: 'red', // any color
      strokeWidth: this.line.strokeWidth,
      opacity: 0.01,
      originX: 'center',
      originY: 'center',
      hoverCursor: 'move',
      selectable: true,
      evented: true,
      hasBorders: false,
      hasControls: false,
      perPixelTargetFind: true,
      isSelectable: true, // for set selectable obj on canvas
      subTypeOf: 'handleContur',
      typeOf: this.typeOf,
      id: this.id
    })
    this.canvasObjects.push(this.line, this.dist, this.handle1, this.handle2, this.handleLine)
  }

  // private method for set coords handleLine as line
  #setLineCoords = function () {
    this.line.set({
      x1: this.handle1.left,
      y1: this.handle1.top,
      x2: this.handle2.left,
      y2: this.handle2.top
    }).setCoords()
  }

  #setHandleLineCoords = function () {
    const point1 = { x: this.handle1.left, y: this.handle1.top }
    const point2 = { x: this.handle2.left, y: this.handle2.top }
    this.handleLine.set({
      x1: this.handle1.left + HANDLE_RADIUS * 1.5 * cosLine(point1, point2),
      y1: this.handle1.top + HANDLE_RADIUS * 1.5 * sinLine(point1, point2),
      x2: this.handle2.left - HANDLE_RADIUS * 1.5 * cosLine(point1, point2),
      y2: this.handle2.top - HANDLE_RADIUS * 1.5 * sinLine(point1, point2)
    }).setCoords()
  }

  #setDist = function () {
    this.dist.set({
      left: (this.handle1.left + this.handle2.left) / 2,
      top: (this.handle1.top + this.handle2.top) / 2,
      text: (Math.sqrt((this.handle1.left - this.handle2.left) ** 2 + (this.handle1.top - this.handle2.top) ** 2) / store.getters.CURRENT_ZOOM / 1000).toFixed(1) + ' ' + i18n.t('m'),
      angle: 180 / Math.PI * Math.atan((this.handle1.top - this.handle2.top) / (this.handle1.left - this.handle2.left))
    }).setCoords()
  }

  is (elemFabric) {
    return (elemFabric === this.line || elemFabric === this.dist || elemFabric === this.handle1 || elemFabric === this.handle2 || elemFabric === this.handleLine) && this
  }

  zooming (scale, point) { // point - point of zooming {x, y}, scale = zoomNew/zoomOld
    this.handle1.set({
      left: point.x + scale * (this.handle1.left - point.x),
      top: point.y + scale * (this.handle1.top - point.y)
    }).setCoords()
    this.handle2.set({
      left: point.x + scale * (this.handle2.left - point.x),
      top: point.y + scale * (this.handle2.top - point.y)
    }).setCoords()
    this.#setLineCoords()
    this.#setHandleLineCoords()
    this.#setDist()
  }

  panning (startPoint, endPoint) { // point - {x, y}
    this.handle1.set({
      left: endPoint.x - startPoint.x + this.handle1.left,
      top: endPoint.y - startPoint.y + this.handle1.top
    }).setCoords()
    this.handle2.set({
      left: endPoint.x - startPoint.x + this.handle2.left,
      top: endPoint.y - startPoint.y + this.handle2.top
    }).setCoords()
    this.#setLineCoords()
    this.#setHandleLineCoords()
    this.#setDist()
  }

  createFirstClick (event) {
    const evt = event.pointer
    this.handle1.set({ left: evt.x, top: evt.y }).setCoords()
    this.handle2.set({ left: evt.x, top: evt.y }).setCoords()
    this.#setDist()
  }

  createWaitNextClick (event) {
    const evt = event.pointer
    this.handle2.set({ left: evt.x, top: evt.y }).setCoords()
    this.#setLineCoords()
    this.#setHandleLineCoords()
    this.#setDist()
  }

  createNextClick (event) {
    this.requireNextClick = false
    const evt = event.pointer
    this.handle2.set({ left: evt.x, top: evt.y }).setCoords()
    this.#setLineCoords()
    this.#setHandleLineCoords()
    this.#setDist()
  }

  selected () {
  }

  unselected () {
  }

  update (clickTarget) {
    if (clickTarget.subTypeOf === 'handle') this.#setHandleLineCoords()
    if (clickTarget === this.handleLine) {
      const startPoint = { x: (this.handle1.left + this.handle2.left) / 2, y: (this.handle1.top + this.handle2.top) / 2 }
      const endPoint = { x: this.handleLine.left, y: this.handleLine.top }
      this.handle1.set({
        left: endPoint.x - startPoint.x + this.handle1.left,
        top: endPoint.y - startPoint.y + this.handle1.top
      }).setCoords()
      this.handle2.set({
        left: endPoint.x - startPoint.x + this.handle2.left,
        top: endPoint.y - startPoint.y + this.handle2.top
      }).setCoords()
    }
    this.#setLineCoords()
    this.#setDist()
  }

  toStoreData () {
    return {
      x1: this.handle1.left,
      y1: this.handle1.top,
      x2: this.handle2.left,
      y2: this.handle2.top,
      zOrder: this.zOrder,
      site: this.site,
      typeOf: this.typeOf,
      id: this.id
    }
  }

  updateParamsFromStore (objData) {
    this.zOrder = objData.zOrder
  }
}
